import {
  Box,
  Flex,
  IconButton,
  Spinner,
  Text,
  useColorModeValue
} from "@chakra-ui/react";
import { useEffect, useRef } from "react";
import { RiExternalLinkLine } from "react-icons/ri";
import { TextGray } from "components/Atomos/Texts/TextGray";
import { useAppDispatch, useTypedSelector } from "store/hooks";
import { obterAnexo } from "store/modules/consultaprevia/actions/obterAnexo";
import { AnexoResumo } from "store/modules/consultaprevia/types";

const formatarTamanho = (bytes: number) => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Nome do anexo para exibição.
 *
 * `fileName` nunca vai para a tela: é o nome do objeto no storage, que o
 * backend monta como `{id do anexo}{extensão}` justamente para não deixar nome
 * de arquivo de usuário definir caminho de bucket. O que o cidadão reconhece é
 * o rótulo — preenchido com o nome original do arquivo quando ninguém digita
 * um no upload.
 */
const nomeAnexo = (anexo: AnexoResumo) =>
  anexo.label?.trim() || "Documento sem nome";

/**
 * Anexos da zona. A listagem traz só metadados: a URL assinada é pedida no
 * clique, em /consulta-previa/anexos/{id} — assinar tudo na listagem gastaria
 * validade antes de alguém querer o arquivo.
 */
export const AnexosZona = ({ anexos }: { anexos: AnexoResumo[] }) => {
  const dispatch = useAppDispatch();
  const borderColor = useColorModeValue("gray.200", "gray.700");
  // Guarda a aba aberta no gesto do clique: abrir só depois da resposta cairia
  // no bloqueador de pop-up.
  const abaRef = useRef<Window | null>(null);

  const { isRequesting, idEmAndamento, errors } = useTypedSelector(
    (state) => state.consultaprevia.obterAnexo
  );

  // Falhou a assinatura: fecha a aba em branco que já tinha sido aberta.
  useEffect(() => {
    if (errors.length > 0 && abaRef.current) {
      abaRef.current.close();
      abaRef.current = null;
    }
  }, [errors]);

  const abrir = (id: string) => {
    abaRef.current = window.open("", "_blank");

    dispatch(
      obterAnexo({
        id,
        onSuccess: (anexo) => {
          if (abaRef.current) {
            abaRef.current.location.href = anexo.urlView;
            abaRef.current = null;
          } else {
            window.open(anexo.urlView, "_blank");
          }
        }
      })
    );
  };

  if (!anexos?.length) {
    return <TextGray fontSize="sm">Nenhum anexo publicado.</TextGray>;
  }

  return (
    <Box>
      {anexos.map((anexo) => (
        <Flex
          key={anexo.id}
          alignItems="center"
          justifyContent="space-between"
          gap="2"
          py="2"
          borderBottom="1px solid"
          borderBottomColor={borderColor}
          _last={{ borderBottom: "none" }}
        >
          <Box minW={0}>
            <Text fontSize="sm" fontWeight="medium" noOfLines={1}>
              {nomeAnexo(anexo)}
            </Text>
            {!!anexo.size && (
              <TextGray fontSize="xs" noOfLines={1}>
                {formatarTamanho(anexo.size)}
              </TextGray>
            )}
          </Box>

          {isRequesting && idEmAndamento === anexo.id ? (
            <Spinner size="sm" color="gray.500" flexShrink={0} />
          ) : (
            <IconButton
              size="sm"
              variant="ghost"
              colorScheme="main"
              flexShrink={0}
              aria-label={`Abrir ${nomeAnexo(anexo)}`}
              icon={<RiExternalLinkLine />}
              onClick={() => abrir(anexo.id)}
            />
          )}
        </Flex>
      ))}
    </Box>
  );
};
